import { FileArrowUpIcon } from "@phosphor-icons/react/dist/csr/FileArrowUp";
import { useRef, useState, type ReactNode } from "react";
import { Card } from "@volca/ui/components/ui/card";
import { Item, ItemContent, ItemMedia } from "@volca/ui/components/ui/item";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { selectUploadFiles } from "./document-utils";
import { acceptAttribute, type UploadFileTypes } from "./upload-file-types";

interface DocumentDropPaneProps {
  maxFiles: number;
  maxSize: number;
  /** Restricts the picker and the drop to these types. Omitted means any file. */
  fileTypes?: UploadFileTypes;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  /** Whether anything is listed below the drop area, which changes the pane's shape. */
  hasContents: boolean;
  /** What has been sent, listed under the drop area in the same pane. */
  children?: ReactNode;
}

const megabytes = (bytes: number) => Math.round(bytes / (1024 * 1024));

// The whole pane is the drop target, rather than a dashed rectangle sitting inside a card
// that holds the list. One surface: the top half invites a file, the bottom half is what
// has landed there. Dragging anywhere over it counts, which is what people try anyway.
export const DocumentDropPane = ({
  maxFiles,
  maxSize,
  fileTypes,
  disabled = false,
  onFiles,
  hasContents,
  children,
}: DocumentDropPaneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [tooBig, setTooBig] = useState<string[]>([]);
  const [tooMany, setTooMany] = useState<string[]>([]);
  const [wrongType, setWrongType] = useState<string[]>([]);

  const accept = (files: File[]) => {
    if (disabled || !files.length) return;

    const selection = selectUploadFiles(files, maxFiles, maxSize, fileTypes);
    setTooBig(selection.oversized.map((file) => file.name));
    setTooMany(selection.excess.map((file) => file.name));
    setWrongType(selection.wrongType.map((file) => file.name));

    if (selection.accepted.length) onFiles(selection.accepted);
  };

  return (
    <Card
      className={cn("w-full gap-0 py-0 transition-colors", {
        "opacity-70": disabled,
      })}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        event.preventDefault();
        setIsDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setIsDragging(false);
        accept(Array.from(event.dataTransfer.files));
      }}
    >
      {/* The dashed outline is what says "drop here": a tinted well inside the pane, so it
          reads as the target rather than a second box competing with the pane. */}
      <Item
        variant="muted"
        render={
          <button
            type="button"
            aria-label="Choose documents to upload"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          />
        }
        className={cn(
          "m-4 flex-col justify-center border-2 border-dashed px-6 py-8",
          {
            "border-brand-line bg-brand-subtle": isDragging,
            "border-line bg-neutral": !isDragging,
            "cursor-not-allowed": disabled,
          },
        )}
        style={{ width: "calc(100% - 2rem)" }}
      >
        <ItemMedia>
          <FileArrowUpIcon className="size-8 text-subtle" />
        </ItemMedia>
        <ItemContent className="items-center gap-1 text-center">
          <Text variant="label">
            Drop documents here, or click to choose them
          </Text>
          <Text variant="caption" tone="muted">
            {fileTypes ? fileTypes.label : "Any file type"}. Up to {maxFiles} at
            a time, {megabytes(fileTypes?.fileMaxBytes ?? maxSize)} MB each
            {fileTypes
              ? `, or a zip up to ${megabytes(fileTypes.archiveMaxBytes)} MB`
              : ""}
            .
          </Text>
        </ItemContent>
      </Item>

      <input
        ref={inputRef}
        type="file"
        aria-label="Upload documents"
        multiple
        accept={fileTypes ? acceptAttribute(fileTypes) : undefined}
        className="sr-only"
        onChange={(event) => {
          accept(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />

      {wrongType.length > 0 && fileTypes && (
        <Stack align="start" gap={4} className="w-full px-4 pb-3">
          <Text variant="caption" className="text-danger">
            {wrongType.join(", ")} {wrongType.length === 1 ? "is" : "are"} not a
            type we can read. Send {fileTypes.label}.
          </Text>
        </Stack>
      )}

      {tooBig.length > 0 && (
        <Stack align="start" gap={4} className="w-full px-4 pb-3">
          <Text variant="caption" className="text-danger">
            {tooBig.join(", ")} {tooBig.length === 1 ? "is" : "are"} too big, so{" "}
            {tooBig.length === 1 ? "it was" : "they were"} not sent. The sizes
            we take are above.
          </Text>
        </Stack>
      )}

      {tooMany.length > 0 && (
        <Stack align="start" gap={4} className="w-full px-4 pb-3">
          <Text variant="caption" className="text-danger">
            {tooMany.join(", ")} {tooMany.length === 1 ? "was" : "were"} not
            sent. You can send up to {maxFiles} files at a time.
          </Text>
        </Stack>
      )}

      {hasContents && (
        <Stack
          gap={4}
          className="w-full border-t border-line px-4 py-1"
          align="stretch"
        >
          {children}
        </Stack>
      )}
    </Card>
  );
};
